import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { clsx } from "clsx";
import Cookie from "js-cookie";
import { flushSync } from "react-dom";
import { Carousel } from "@bloomberg/intercom";
import { useRegUIClient } from "@foundation/reg-ui-client";

import {
  CAROUSEL_CARD_GAP,
  CAROUSEL_CARD_WIDTH,
  COPY_TEXT,
  NUMBER_OF_STORIES_PER_TOPIC,
  SHOW_TOPIC_PICKER_COOKIE,
  TOPIC_GROUPS,
} from "../constants";
import { HideButton } from "../HideButton";
import { ShowButton } from "../ShowButton";
import { SingleTopicStoriesLoading } from "../SingleTopicStories";
import TagPicker from "../TagPicker";
import { trackClick } from "../tracking";
import { UserTopicPanel } from "../UserTopicPanel";
import {
  addFollowInFront,
  addFollowInPlace,
  getFollowedTopicIdsMap,
  getOptimisticTopicFollow,
} from "../utils";

import {
  LoadingTopicCardProps,
  PanelTypes,
  SingleTopicStoriesPanelProps,
} from "../types";
import styles from "./userTopicsStoriesDesktop.module.css";
import { useTopicsStories } from "~hooks/useTopicsStories";
import intercom from "~config/intercom";
import { addPageAction, logError } from "~utils/newrelic/newrelicBrowser";

const TRACKING_ID = "[lineup]topic_module";

export const UserTopicsStoriesDesktop = () => {
  const bconnectId = useRegUIClient().session?.userInfo?.userId ?? "";
  const isSignedIn = Boolean(bconnectId);
  const rootContainerRef = useRef(null);
  const [isPickerPanelOpen, setIsPickerPanelOpen] = useState(true);

  const carouselPaginationControlsHook =
    Carousel.useCarouselPaginationControls();

  const { setControlledPageIndex } = carouselPaginationControlsHook;

  /* TODO: integrate excluded stories logic from lineup
     currently lineup gets it from curator
   */
  const storyIdsToExclude = "";

  useEffect(() => {
    const cookie = Cookie.get(SHOW_TOPIC_PICKER_COOKIE);

    if (cookie) {
      setIsPickerPanelOpen(cookie === "true");
    }
  }, []);

  const {
    topicsError,
    topicsStoriesError,
    isLoading,
    setTopics,
    topics,
    topicsStories,
  } = useTopicsStories({ bconnectId, storyIdsToExclude });

  const followTopicCallback = useCallback(
    async (topicId: string, addInPlace?: boolean) => {
      addPageAction("followTopic", {
        topicId,
      });
      if (bconnectId) {
        flushSync(() => {
          setTopics((currentTopics) =>
            addInPlace
              ? addFollowInPlace(
                  currentTopics,
                  getOptimisticTopicFollow(topicId)
                )
              : addFollowInFront(
                  currentTopics,
                  getOptimisticTopicFollow(topicId)
                )
          );
        });
        try {
          const isTopicFollowSuccess = await intercom.followTopic({
            topicId,
          });

          if (isTopicFollowSuccess) {
            trackClick("follow", `${topicId}_topicpanel`);
          }
        } catch (e) {
          console.warn(e);
          trackClick("unsuccessful follow", `${topicId}_topicpanel`);
          logError(new Error("[followTopic] - error following topic"), {
            bconnectId,
            topicId,
          });

          flushSync(() =>
            setTopics((currentTopics) =>
              currentTopics.map((currentTopic) =>
                currentTopic.id === topicId
                  ? {
                      ...currentTopic,
                      isFollowedByUser: false,
                    }
                  : currentTopic
              )
            )
          );
        }
      }
    },
    [bconnectId, setTopics]
  );

  const unfollowTopicCallback = useCallback(
    async (topicId: string) => {
      addPageAction("unFollowTopic", {
        topicId,
      });
      if (bconnectId) {
        flushSync(() =>
          setTopics((currentTopics) =>
            currentTopics.filter((currentTopic) => currentTopic.id !== topicId)
          )
        );
        try {
          const isTopicUnfollowSuccess = await intercom.deleteTopic({
            topicId,
          });

          if (isTopicUnfollowSuccess) {
            trackClick("unfollow", `${topicId}_topicpanel`);
          }
        } catch (e) {
          console.warn(e);
          trackClick("unsuccessful unfollow", `${topicId}_topicpanel`);
          // logError(new Error("[unfollowTopic] - error unfollowing topic"), {
          //   bconnectId,
          //   topicId,
          // });

          flushSync(() =>
            setTopics((currentTopics) =>
              addFollowInFront(currentTopics, getOptimisticTopicFollow(topicId))
            )
          );
        }
      }
    },
    [bconnectId, setTopics]
  );

  // Since the topic picker is the first card in the carousel, hiding
  // and showing is just a matter of paginating to and from the topic picker
  const onShowTopicPickerInCarousel = useCallback(() => {
    trackClick("toggle_topic_picker", "picker_open");
    Cookie.set(SHOW_TOPIC_PICKER_COOKIE, "true", {
      expires: 365,
    });
    setIsPickerPanelOpen(true);
  }, [setIsPickerPanelOpen]);

  const onHideTopicPickerInCarousel = useCallback(() => {
    trackClick("toggle_topic_picker", "picker_close");
    Cookie.set(SHOW_TOPIC_PICKER_COOKIE, "false", {
      expires: 365,
    });
    setControlledPageIndex(0);
    setIsPickerPanelOpen(false);
  }, [setIsPickerPanelOpen]);

  const topicCardProps: (
    | SingleTopicStoriesPanelProps
    | LoadingTopicCardProps
  )[] = useMemo(
    () =>
      topics.map((topic) => {
        if (!topicsStories[topic.id]) {
          return {
            type: PanelTypes.LoadingTopicCard,
            props: {},
          };
        }

        return {
          type: PanelTypes.TopicCard,
          props: {
            topic,
            stories: topicsStories[topic.id],
            rootRef: rootContainerRef,
            followTopicCallback,
            isSignedIn,
          },
        };
      }),
    [topics, topicsStories, followTopicCallback, isSignedIn]
  );

  const childComponentPropsList = useMemo(
    () => topicCardProps,
    [topicCardProps]
  );

  if (topicsError) {
    console.warn(
      `[UserTopicsStoriesDesktop] An error occurred fetching topics: ${topicsError}`
    );
  }

  if (topicsStoriesError) {
    console.warn(
      `[UserTopicsStoriesDesktop] An error occurred fetching topicsStories: ${topicsStoriesError}`
    );
  }

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <SingleTopicStoriesLoading
          numberOfRows={NUMBER_OF_STORIES_PER_TOPIC}
          length={3}
        />
      </div>
    );
  }

  if (!topicsStories || !topics) {
    return null;
  }

  return (
    <div
      className={styles.userTopicsStoriesDesktop}
      ref={rootContainerRef}
      style={
        {
          "--carousel-card-width": `${
            CAROUSEL_CARD_GAP + CAROUSEL_CARD_WIDTH
          }px`,
        } as React.CSSProperties
      }
    >
      <div className={styles.header}>
        <h3 className={styles.title}>Market News</h3>
        {isPickerPanelOpen ? (
          /** TODO [MEDIAUI-151]: replace with newer button in lineup when new token designs are available */
          <HideButton onClick={onHideTopicPickerInCarousel} />
        ) : (
          <ShowButton onClick={onShowTopicPickerInCarousel} />
        )}
      </div>
      <div className={styles.flexContainer}>
        <div
          className={clsx(
            styles.tagPickerContainer,
            !isPickerPanelOpen && styles.pickerClosedPicker
          )}
        >
          <TagPicker
            onUnselect={unfollowTopicCallback}
            onSelect={followTopicCallback}
            selected={getFollowedTopicIdsMap(topics)}
            label="Follow Topics"
            description={COPY_TEXT}
            itemGroups={TOPIC_GROUPS}
            isSignedIn={isSignedIn}
          />
        </div>

        <div
          className={clsx(
            styles.carouselContainer,
            !isPickerPanelOpen && styles.pickerClosedCarousel
          )}
        >
          <Carousel.WithTracking
            IterableComponent={UserTopicPanel}
            componentPropsList={childComponentPropsList}
            tileProperties={{
              gap: CAROUSEL_CARD_GAP,
              width: CAROUSEL_CARD_WIDTH,
            }}
            trackingId={TRACKING_ID}
            paginationProperties={{ fullWidth: true }}
            {...carouselPaginationControlsHook}
          />
        </div>
      </div>
    </div>
  );
};
